import React, { useMemo, useState } from 'react';
import { Box, Typography, Grid, Dialog, Button, Link } from '@mui/material';
import {
    MovieFilter,
    Timer,
    CheckCircle,
    VideoLibrary,
    PlayCircle,
    LightbulbOutlined,
    FolderOpenOutlined,
    DownloadOutlined,
    HandshakeOutlined,
    PhoneAndroidOutlined,
    RepeatOutlined,
    WarningAmberOutlined,
} from '@mui/icons-material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import type { IDeliverableRound } from '../../redux/buzzbridge/types';
import { useSelector } from 'react-redux';
import { ICampaign } from '../../redux/buzzbridge/types';
import type { RootState } from '../../redux/root-reducer';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { RichTextViewer } from '../Common/RichTextViewer';
import { VideoModalPlayer } from '../../pages/BrandDashboard/campaignDetail/VideoModalPlayer';
import { DeliverableBulletList } from './DeliverableBulletList';
import { CampaignCommissionDetailBlock } from './CampaignCommissionDetailBlock';
import { CampaignPremiumAccessGate } from './CampaignPremiumAccessGate';
import { CampaignSyncUploadNotice } from './CampaignSyncUploadNotice';
import { resolveCampaignCreatorRequirements } from '../../utils/ugcCampaignConfig';
import type { IInfluencerTier } from '../../types/global/helper';

interface Props {
    campaign: ICampaign;
    isSignedIn?: boolean;
    /** Whether required platforms are OAuth auto-linked (null = unknown). */
    isAutoLinked?: boolean | null;
    /** Live-reconciled premium access (after get-my-plan); falls back to campaign.premium_access. */
    premiumAccess?: ICampaign['premium_access'];
    onPremiumSubscribe?: () => void;
    onPremiumConnectAccounts?: () => void;
    onPremiumSignIn?: () => void;
    onSyncConnectAccounts?: () => void;
    onSyncSignIn?: () => void;
}

const assetFileLabel = (url: string, description?: string) => {
    if (description?.trim()) return description.trim();
    try {
        const path = new URL(url).pathname;
        const name = decodeURIComponent(path.split('/').pop() || '');
        return name || 'Campaign asset';
    } catch {
        return 'Campaign asset';
    }
};

const SectionHeading: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <Typography
        sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color,
            mb: 3,
            fontFamily: FONT_ENUM.MAIN_BOLD,
        }}>
        {children}
    </Typography>
);

export const CampaignDetailContent: React.FC<Props> = ({
    campaign,
    isSignedIn,
    isAutoLinked,
    premiumAccess: premiumAccessProp,
    onPremiumSubscribe,
    onPremiumConnectAccounts,
    onPremiumSignIn,
    onSyncConnectAccounts,
    onSyncSignIn,
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const premiumAccess = premiumAccessProp ?? campaign.premium_access;
    const objectives = campaign.objectives ?? [];
    const deliverables = campaign.deliverables ?? [];
    const ideas = campaign.ideas ?? [];
    const sampleVideos = campaign.sample_videos ?? [];
    const assets = campaign.assets ?? [];
    const configTiers = useSelector((state: RootState) => state.configuration.influencerTiers) as IInfluencerTier[];
    const creatorRequirements = useMemo(
        () => resolveCampaignCreatorRequirements(campaign, configTiers),
        [campaign, configTiers],
    );
    const requirements = useMemo(() => {
        const custom = campaign.requirements ?? [];
        const tierLines = creatorRequirements?.lines ?? [];
        // Tier eligibility first, then brand-written requirements (dedupe exact matches).
        const merged = [...tierLines];
        for (const req of custom) {
            const text = String(req || '').trim();
            if (text && !merged.includes(text)) merged.push(text);
        }
        return merged;
    }, [campaign.requirements, creatorRequirements]);

    const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(premiumAccess?.premium && !premiumAccess?.can_apply) ||
            (campaign.campaign_type === 'sync_upload' && campaign.sync_publish_date) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <CampaignPremiumAccessGate
                        access={premiumAccess}
                        isSignedIn={isSignedIn}
                        onSubscribe={onPremiumSubscribe}
                        onConnectAccounts={onPremiumConnectAccounts}
                        onSignIn={onPremiumSignIn}
                        sx={{ mb: 0 }}
                    />

                    {campaign.campaign_type === 'sync_upload' && campaign.sync_publish_date ? (
                        <CampaignSyncUploadNotice
                            syncPublishDate={campaign.sync_publish_date}
                            isAutoLinked={isAutoLinked}
                            isSignedIn={isSignedIn}
                            onConnectAccounts={onSyncConnectAccounts}
                            onSignIn={onSyncSignIn}
                        />
                    ) : null}
                </Box>
            ) : null}

            {/* Brief */}
            {campaign.description && (
                <Box>
                    <SectionHeading color={t.highlight}>The Brief</SectionHeading>
                    <Box
                        sx={{
                            '& .rich-text-wrapper .ql-editor': {
                                fontSize: { xs: 16, md: 20 },
                                lineHeight: 1.7,
                                fontWeight: 300,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            },
                        }}>
                        <RichTextViewer
                            content={campaign.description}
                            textColor={t.subtle_white}
                            textStyle={{ padding: 0 }}
                        />
                    </Box>
                    {campaign.campaign_type === 'commission' && (
                        <Box sx={{ mt: 3 }}>
                            <CampaignCommissionDetailBlock campaign={campaign} accent="#ffc965" />
                        </Box>
                    )}
                    {campaign.app_install?.enabled && (
                        <Box
                            sx={{
                                mt: 3,
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,201,101,0.06)',
                                border: '1px solid rgba(255,201,101,0.18)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneAndroidOutlined sx={{ fontSize: 16, color: '#ffc965' }} />
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.12em',
                                        color: '#ffc965',
                                    }}>
                                    App / web install campaign
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: 'rgba(255,255,255,0.75)',
                                    lineHeight: 1.6,
                                }}>
                                {campaign.app_install.kind === 'web_visit'
                                    ? "Drive traffic to the brand's website using your unique referral link. Your earnings are tracked automatically — no video or photo upload needed."
                                    : 'Drive app installs using your unique referral link. Your earnings are tracked automatically — no video or photo upload needed.'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: 'rgba(255,201,101,0.8)',
                                }}>
                                Paid after each verified install / visit is attributed to your link.
                            </Typography>
                        </Box>
                    )}
                    {campaign.campaign_type === 'direct_hire' && (
                        <Box
                            sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,201,101,0.06)',
                                border: '1px solid rgba(255,201,101,0.18)',
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <HandshakeOutlined sx={{ fontSize: 13, color: '#ffc965' }} />
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.12em',
                                        color: '#ffc965',
                                    }}>
                                    Direct hire
                                </Typography>
                            </Box>
                            <Typography
                                sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, mb: 0.5 }}>
                                You don't need to publish to social for this campaign.
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>
                                Submit your content for review and the brand will handle distribution. Payout is issued
                                after your submission is approved.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Recurrent deliverables schedule */}
            {campaign.recurrent_deliverables && (campaign.deliverable_rounds ?? []).length > 0 && (
                <ScheduleSection rounds={campaign.deliverable_rounds as IDeliverableRound[]} highlight={t.highlight} />
            )}

            {/* Creative Ideas */}
            {ideas.length > 0 && (
                <Box>
                    <SectionHeading color={t.highlight}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LightbulbOutlined sx={{ fontSize: 14 }} />
                            Creative Ideas
                        </Box>
                    </SectionHeading>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {ideas.map((idea, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <LightbulbOutlined sx={{ color: t.highlight, fontSize: 16, mt: 0.5 }} />
                                <Typography sx={{ color: t.subtle_white, fontSize: 14, lineHeight: 1.6 }}>
                                    {idea}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Feature cards — objectives + deliverables */}
            {(objectives.length > 0 || deliverables.length > 0) && (
                <Grid container spacing={2}>
                    {objectives.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 3,
                                    bgcolor: t.deep_grey_lighter,
                                    borderRadius: 3,
                                    borderLeft: `2px solid ${t.highlight}50`,
                                    height: '100%',
                                }}>
                                <MovieFilter sx={{ color: t.highlight, mb: 2 }} />
                                <Typography
                                    sx={{
                                        color: t.true_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        mb: 1.5,
                                    }}>
                                    Objectives
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {objectives.map((item, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <FlagOutlinedIcon
                                                sx={{ fontSize: 16, color: t.highlight, flexShrink: 0, mt: '2px' }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 14,
                                                    color: t.subtle_white,
                                                    lineHeight: 1.6,
                                                }}>
                                                {item}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    )}
                    {deliverables.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 3,
                                    bgcolor: t.deep_grey_lighter,
                                    borderRadius: 3,
                                    borderLeft: `2px solid ${t.btnCustomColor}50`,
                                    height: '100%',
                                }}>
                                <Timer sx={{ color: t.btnCustomColor, mb: 2 }} />
                                <Typography
                                    sx={{
                                        color: t.true_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        mb: 1.5,
                                    }}>
                                    Deliverables
                                </Typography>
                                <DeliverableBulletList deliverables={deliverables} textColor={t.subtle_white} />
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Sample Videos */}
            {sampleVideos.length > 0 && (
                <Box>
                    <SectionHeading color={t.highlight}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VideoLibrary sx={{ fontSize: 14 }} />
                            Sample Videos
                        </Box>
                    </SectionHeading>
                    <Grid container spacing={2}>
                        {sampleVideos.map((video, i) => (
                            <Grid item xs={12} md={6} key={video._id ?? i}>
                                <Box
                                    onClick={() => setActiveVideoUrl(video.url)}
                                    sx={{
                                        position: 'relative',
                                        bgcolor: '#000',
                                        borderRadius: 3,
                                        borderLeft: `2px solid ${t.highlight}50`,
                                        cursor: 'pointer',
                                        aspectRatio: '16/9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '&:hover .play-overlay': { opacity: 1 },
                                    }}>
                                    <video
                                        src={video.url}
                                        preload="metadata"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: 12,
                                            display: 'block',
                                            objectFit: 'cover',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                    <Box
                                        className="play-overlay"
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(0,0,0,0.35)',
                                            borderRadius: 3,
                                            opacity: 0.85,
                                            transition: 'opacity 0.2s',
                                        }}>
                                        <PlayCircle sx={{ fontSize: 52, color: '#fff', opacity: 0.9 }} />
                                    </Box>
                                    {video.title && (
                                        <Typography
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                left: 12,
                                                right: 12,
                                                fontSize: 12,
                                                color: 'rgba(255,255,255,0.8)',
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            }}>
                                            {video.title}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Campaign Assets */}
            {assets.length > 0 && (
                <Box>
                    <SectionHeading color={t.highlight}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FolderOpenOutlined sx={{ fontSize: 14 }} />
                            Campaign Assets
                        </Box>
                    </SectionHeading>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {assets.map((asset, i) => {
                            const label = assetFileLabel(asset.url, asset.description);
                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        p: 2.5,
                                        bgcolor: t.deep_grey_lighter,
                                        borderRadius: 3,
                                        borderLeft: `2px solid ${t.btnCustomColor}50`,
                                        flexDirection: { xs: 'column', sm: 'row' },
                                    }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                color: t.true_white,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 14,
                                                mb: asset.requirements?.length ? 0.5 : 0,
                                            }}>
                                            {label}
                                        </Typography>
                                        {asset.requirements?.map((req, j) => (
                                            <Typography
                                                key={j}
                                                sx={{ color: t.subtle_white, fontSize: 12, lineHeight: 1.5 }}>
                                                {req}
                                            </Typography>
                                        ))}
                                    </Box>
                                    <Button
                                        component={Link}
                                        href={asset.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                        variant="outlined"
                                        startIcon={<DownloadOutlined />}
                                        sx={{
                                            flexShrink: 0,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            color: t.highlight,
                                            borderColor: `${t.highlight}66`,
                                            '&:hover': {
                                                borderColor: t.highlight,
                                                bgcolor: `${t.highlight}12`,
                                            },
                                        }}>
                                        Download
                                    </Button>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Requirements checklist */}
            {requirements.length > 0 && (
                <Box>
                    <SectionHeading color={t.highlight}>Requirements</SectionHeading>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {requirements.map((req, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <CheckCircle sx={{ color: t.btnCustomColor, fontSize: 16, mt: 0.5 }} />
                                <Typography sx={{ color: t.subtle_white, fontSize: 14 }}>{req}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Video player dialog */}
            <Dialog
                open={!!activeVideoUrl}
                onClose={() => setActiveVideoUrl(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { bgcolor: '#000', borderRadius: 3 } }}>
                {activeVideoUrl && (
                    <VideoModalPlayer
                        src={activeVideoUrl}
                        onClose={() => setActiveVideoUrl(null)}
                        maxVideoHeight="75vh"
                    />
                )}
            </Dialog>
        </Box>
    );
};

const SCHEDULE_PAGE_SIZE = 4;

function ScheduleSection({ rounds, highlight }: { rounds: IDeliverableRound[]; highlight: string }) {
    const [page, setPage] = useState(0);
    const totalPages = Math.ceil(rounds.length / SCHEDULE_PAGE_SIZE);
    const pageRounds = rounds.slice(page * SCHEDULE_PAGE_SIZE, (page + 1) * SCHEDULE_PAGE_SIZE);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: highlight }}>
                    <RepeatOutlined sx={{ fontSize: 14 }} />
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: highlight,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                        }}>
                        Delivery Schedule
                    </Typography>
                </Box>
                {totalPages > 1 && (
                    <Typography
                        sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        {page * SCHEDULE_PAGE_SIZE + 1}–{Math.min((page + 1) * SCHEDULE_PAGE_SIZE, rounds.length)} of{' '}
                        {rounds.length}
                    </Typography>
                )}
            </Box>
            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', mb: 2, lineHeight: 1.6 }}>
                This campaign has multiple submission deadlines. Each has its own content requirements.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {pageRounds.map((round, i) => {
                    const daysUntil = round.delivery_date
                        ? Math.ceil((new Date(round.delivery_date).getTime() - Date.now()) / 86400000)
                        : null;
                    const isUrgent = daysUntil !== null && daysUntil >= 0 && daysUntil < 7;
                    const isPast = daysUntil !== null && daysUntil < 0;
                    const accentColor = isUrgent ? '#ef4444' : isPast ? 'rgba(255,255,255,0.2)' : highlight;
                    const bgColor = isUrgent
                        ? 'rgba(239,68,68,0.07)'
                        : isPast
                        ? 'rgba(255,255,255,0.03)'
                        : 'rgba(255,201,101,0.05)';
                    return (
                        <Box
                            key={page * SCHEDULE_PAGE_SIZE + i}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${accentColor}33`,
                                bgcolor: bgColor,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2,
                            }}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    bgcolor: `${accentColor}22`,
                                    border: `1px solid ${accentColor}55`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: accentColor }}>
                                    {round.round_number}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: isPast ? 'rgba(255,255,255,0.35)' : '#fff',
                                        }}>
                                        {round.label || `Delivery ${round.round_number}`}
                                    </Typography>
                                    {isUrgent && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                bgcolor: 'rgba(239,68,68,0.15)',
                                                border: '1px solid rgba(239,68,68,0.4)',
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.25,
                                            }}>
                                            <WarningAmberOutlined sx={{ fontSize: 11, color: '#ef4444' }} />
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: '#ef4444',
                                                }}>
                                                {daysUntil === 0 ? 'Due today' : `${daysUntil}d left`}
                                            </Typography>
                                        </Box>
                                    )}
                                    {isPast && (
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: 'rgba(255,255,255,0.3)',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Closed
                                        </Typography>
                                    )}
                                </Box>
                                {round.delivery_date && (
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: isUrgent ? '#fca5a5' : 'rgba(255,255,255,0.45)',
                                            mb: round.deliverables?.length ? 1 : 0,
                                        }}>
                                        Deadline:{' '}
                                        {new Date(round.delivery_date).toLocaleDateString('en-US', {
                                            weekday: 'short',
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                        })}
                                        {!isUrgent && daysUntil !== null && !isPast && (
                                            <Typography
                                                component="span"
                                                sx={{ ml: 1, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                                                ({daysUntil}d away)
                                            </Typography>
                                        )}
                                    </Typography>
                                )}
                                {(round.deliverables ?? []).length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {round.deliverables.map((d, j) => (
                                            <Typography
                                                key={j}
                                                sx={{
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: isPast ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)',
                                                    bgcolor: 'rgba(255,255,255,0.06)',
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    borderRadius: 1,
                                                    px: 1,
                                                    py: 0.25,
                                                }}>
                                                {d.count > 1 ? `${d.count}× ` : ''}
                                                {d.type.replace(/_/g, ' ')}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                    <Button
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        sx={{
                            color: highlight,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'none',
                            fontSize: 12,
                            px: 0,
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                        }}>
                        ← Previous
                    </Button>
                    <Typography
                        sx={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        Page {page + 1} of {totalPages}
                    </Typography>
                    <Button
                        size="small"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(p => p + 1)}
                        sx={{
                            color: highlight,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'none',
                            fontSize: 12,
                            px: 0,
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                        }}>
                        Next →
                    </Button>
                </Box>
            )}
        </Box>
    );
}
